// Credit to https://github.com/LiosK/UUID.js/blob/master/src/uuid.core.js

/**
 * UUID.core.js: A small subset of UUID.js, the RFC-compliant UUID generator for JavaScript.
 *
 * @fileOverview
 * @author  LiosK
 * @version v3.3.0
 * @license The MIT License: Copyright (c) 2010-2016 LiosK.
 */

/** @constructor */

export const generateUUID: () => string = () => {
    /**
     * Returns an unsigned x-bit random integer.
     * @param {int} x A positive integer ranging from 0 to 53, inclusive.
     * @returns {int} An unsigned x-bit random integer (0 <= f(x) < 2^x).
     */
    const rand = (x: number) => {
        // _getRandomInt
        if (x < 0) {
            return NaN;
        }
        if (x <= 30) {
            return 0 | (Math.random() * (1 << x));
        }
        if (x <= 53) {
            return (0 | (Math.random() * (1 << 30))) + (0 | (Math.random() * (1 << (x - 30)))) * (1 << 30);
        }
        return NaN;
    };

    /**
     * Converts an integer to a zero-filled hexadecimal string.
     * @param {int} num
     * @param {int} length
     * @returns {string}
     */
    const hex = (num: number, length: number) => {
        // _hexAligner
        let str = num.toString(16);
        let i = length - str.length;
        let z = '0';

        for (; i > 0; i >>>= 1, z += z) {
            if (i & 1) {
                str = z + str;
            }
        }
        return str;
    };

    /**
     * The simplest function to get an UUID string.
     * @returns {string} A version 4 UUID string.
     */
    return `${
        hex(rand(32), 8) // time_low
    }-${
        hex(rand(16), 4) // time_mid
    }-${
        hex(0x4000 | rand(12), 4) // time_hi_and_version
    }-${
        hex(0x8000 | rand(14), 4) // clock_seq_hi_and_reserved clock_seq_low
    }-${hex(rand(48), 12)}`; // node
};

export default generateUUID;
